import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

/**
 * Gates foodpreneur (spaza) AND supplier users behind admin approval.
 * Admin, brand, community, logistics, data_capturer roles bypass this check.
 *
 * Suppliers are matched by contact_email on the supplier_applications table,
 * since the public application flow does not currently create a linked user_id.
 */
export function FoodpreneurApprovalGate({ children }: { children: React.ReactNode }) {
  const { session, profile, loading } = useAuth();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [approved, setApproved] = useState(false);

  const skipsApproval =
    profile?.is_admin ||
    profile?.role === 'admin' ||
    profile?.role === 'brand' ||
    profile?.role === 'community' ||
    profile?.role === 'logistics' ||
    profile?.role === 'data_capturer';

  useEffect(() => {
    if (loading) return;
    if (!session?.user?.id || skipsApproval) {
      setChecking(false);
      return;
    }
    (async () => {
      const userId = session.user.id;
      const email = (session.user.email || profile?.email || '').toLowerCase();

      // Check supplier application by email (suppliers are gated until approved).
      let supplierApproved: boolean | null = null;
      if (email) {
        const { data: sup } = await supabase
          .from('supplier_applications')
          .select('status')
          .eq('submitter_id', userId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (sup) supplierApproved = sup.status === 'approved';
      }

      // Check foodpreneur profile.
      const { data: fp } = await supabase
        .from('foodpreneur_profiles')
        .select('approval_status')
        .eq('user_id', userId)
        .maybeSingle();

      // If user has a supplier application, that decides; otherwise fall back to foodpreneur status.
      const isApproved =
        supplierApproved === true ||
        (supplierApproved === null && (fp?.approval_status as any) === 'approved');

      setApproved(isApproved);
      setChecking(false);
    })();
  }, [session?.user?.id, loading, skipsApproval, profile?.email, session?.user?.email]);

  if (loading || checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (skipsApproval || approved) return <>{children}</>;
  if (location.pathname === '/account/pending-approval') return <>{children}</>;
  return <Navigate to="/account/pending-approval" replace />;
}
